import React, { useState, useEffect, useMemo } from 'react';
import { dailyReportService, DailyReportRow, DailyReportData } from '../services/daily-report.service';
import './DailyReportPage.css';

type TabKey = '홀딩스계열' | '제강계열' | '공통';
type SortField = 'device_name' | 'ip_address' | 'conn_status' | 'uptime' | 'cpu' | 'mem';
type SortOrder = 'asc' | 'desc';

const TAB_LABELS: { key: TabKey; label: string }[] = [
  { key: '홀딩스계열', label: '세아홀딩스 계열' },
  { key: '제강계열', label: '세아제강지주 계열' },
  { key: '공통', label: '공통' },
];

function parsePeriod(period: string) {
  const date = `${period.slice(0, 4)}-${period.slice(4, 6)}-${period.slice(6, 8)}`;
  const hour = period.slice(8, 10) === '04' ? '04:00' : '16:00';
  return { date, hour };
}

function groupPeriodsByDate(periods: string[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const p of periods) {
    const date = `${p.slice(0, 4)}-${p.slice(4, 6)}-${p.slice(6, 8)}`;
    if (!map.has(date)) map.set(date, []);
    map.get(date)!.push(p);
  }
  return map;
}

export default function DailyReportPage() {
  const [periods, setPeriods] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [reportData, setReportData] = useState<DailyReportData | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('홀딩스계열');
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('device_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [connFilter, setConnFilter] = useState<'전체' | '정상' | '비정상'>('전체');
  const [myGroups, setMyGroups] = useState<string[]>([]);

  useEffect(() => {
    dailyReportService.getMyGroups().then(setMyGroups).catch(() => {});
    dailyReportService.getPeriods().then((list) => {
      setPeriods(list);
      if (list.length > 0) setSelectedPeriod(list[0]);
    });
  }, []);

  useEffect(() => {
    if (!selectedPeriod) return;
    setIsLoading(true);
    dailyReportService.getData(selectedPeriod)
      .then(setReportData)
      .catch((e) => alert('데이터 조회 실패: ' + e.message))
      .finally(() => setIsLoading(false));
  }, [selectedPeriod]);

  const periodsByDate = useMemo(() => groupPeriodsByDate(periods), [periods]);
  const dateList = useMemo(() => Array.from(periodsByDate.keys()), [periodsByDate]);

  const selectedDate = selectedPeriod
    ? `${selectedPeriod.slice(0, 4)}-${selectedPeriod.slice(4, 6)}-${selectedPeriod.slice(6, 8)}`
    : '';
  const selectedHour = selectedPeriod ? selectedPeriod.slice(8, 10) : '';

  const hoursForDate = selectedDate ? (periodsByDate.get(selectedDate) ?? []) : [];

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return '';
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  const filteredRows = useMemo(() => {
    if (!reportData) return [];
    let rows: DailyReportRow[] = reportData.groups[activeTab] ?? [];

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.device_name?.toLowerCase().includes(q) ||
          r.ip_address?.toLowerCase().includes(q),
      );
    }

    if (connFilter !== '전체') {
      rows = rows.filter((r) => r.conn_status === connFilter);
    }

    return [...rows].sort((a, b) => {
      const av = (a[sortField] ?? '') as string;
      const bv = (b[sortField] ?? '') as string;
      if (['cpu', 'mem', 'uptime'].includes(sortField)) {
        const numA = parseFloat(av) || 0;
        const numB = parseFloat(bv) || 0;
        return sortOrder === 'asc' ? numA - numB : numB - numA;
      }
      const cmp = av.localeCompare(bv, 'ko', { numeric: true });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [reportData, activeTab, search, sortField, sortOrder, connFilter]);

  const totalCount = reportData
    ? Object.values(reportData.groups).reduce((s, g) => s + g.length, 0)
    : 0;

  const abnormalCount = reportData
    ? Object.values(reportData.groups)
        .flat()
        .filter((r) => r.conn_status !== '정상').length
    : 0;

  return (
    <div className="daily-report-page">
      <div className="dr-header">
        <h2>일일보고서</h2>

        <div className="dr-filters">
          <div className="dr-date-group">
            <label>날짜</label>
            <select
              value={selectedDate}
              onChange={(e) => {
                const hrs = periodsByDate.get(e.target.value) ?? [];
                setSelectedPeriod(hrs[0] ?? '');
              }}
            >
              {dateList.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="dr-hour-group">
            {['04', '16'].map((h) => {
              const p = hoursForDate.find((x) => x.slice(8, 10) === h);
              return (
                <button
                  key={h}
                  className={`dr-hour-btn${selectedHour === h ? ' active' : ''}${!p ? ' disabled' : ''}`}
                  onClick={() => p && setSelectedPeriod(p)}
                  disabled={!p}
                >
                  {h === '04' ? '04:00' : '16:00'}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {reportData && (
        <div className="dr-status-bar">
          <span className={`dr-collection-badge ${reportData.collectionStatus === 'SUCCESS' ? 'success' : reportData.collectionStatus === 'FAILED' ? 'failed' : 'unknown'}`}>
            {reportData.collectionStatus === 'SUCCESS' ? '✅ 수집 성공'
              : reportData.collectionStatus === 'FAILED' ? '❌ 수집 실패'
              : '● 수집 상태 없음'}
          </span>
          {reportData.collectedAt && (
            <span className="dr-collected-at">
              수집 시각: {new Date(reportData.collectedAt).toLocaleString('ko-KR')}
            </span>
          )}
          <span className="dr-total">전체 {totalCount}대</span>
          {abnormalCount > 0 && (
            <span className="dr-abnormal">비정상 {abnormalCount}대</span>
          )}
        </div>
      )}

      <div className="dr-toolbar">
        <div className="dr-tabs">
          {TAB_LABELS.filter(({ key }) => key === '공통' || myGroups.includes(key)).map(({ key, label }) => (
            <button
              key={key}
              className={`dr-tab${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
              {reportData && (
                <span className="dr-tab-count">{reportData.groups[key]?.length ?? 0}</span>
              )}
            </button>
          ))}
        </div>

        <div className="dr-search-group">
          <input
            type="text"
            placeholder="장비명, IP 검색..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dr-search"
          />
          <select
            value={connFilter}
            onChange={(e) => setConnFilter(e.target.value as any)}
            className="dr-conn-filter"
          >
            <option value="전체">전체</option>
            <option value="정상">정상</option>
            <option value="비정상">비정상</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="dr-loading">데이터를 불러오는 중...</div>
      ) : (
        <div className="dr-table-wrap">
          <table className="dr-table">
            <thead>
              <tr>
                <th>No</th>
                <th className="sortable" onClick={() => handleSort('device_name')}>
                  장비명{sortIcon('device_name')}
                </th>
                <th className="sortable" onClick={() => handleSort('ip_address')}>
                  IP주소{sortIcon('ip_address')}
                </th>
                <th className="sortable" onClick={() => handleSort('conn_status')}>
                  접속상태{sortIcon('conn_status')}
                </th>
                <th className="sortable" onClick={() => handleSort('uptime')}>
                  UPTIME{sortIcon('uptime')}
                </th>
                <th className="sortable" onClick={() => handleSort('cpu')}>
                  CPU(%){sortIcon('cpu')}
                </th>
                <th className="sortable" onClick={() => handleSort('mem')}>
                  MEM(%){sortIcon('mem')}
                </th>
                <th>DISK</th>
                <th>INTERFACE</th>
                <th>최종 접속 로그</th>
                <th>서비스 상태</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="dr-no-data">데이터가 없습니다.</td>
                </tr>
              ) : (
                filteredRows.map((row, i) => (
                  <tr key={i} className={row.conn_status !== '정상' ? 'dr-row-abnormal' : ''}>
                    <td>{i + 1}</td>
                    <td className="dr-device-name">{row.device_name}</td>
                    <td className="dr-ip">{row.ip_address}</td>
                    <td>
                      <span className={`dr-conn-badge ${row.conn_status === '정상' ? 'normal' : 'abnormal'}`}>
                        {row.conn_status}
                      </span>
                    </td>
                    <td>{row.uptime}</td>
                    <td className={Number(row.cpu) >= 80 ? 'dr-warn' : ''}>{row.cpu}</td>
                    <td className={Number(row.mem) >= 80 ? 'dr-warn' : ''}>{row.mem}</td>
                    <td className="dr-disk">{row.disk}</td>
                    <td className={row.interface_st?.includes('비정상') ? 'dr-warn' : ''}>
                      {row.interface_st}
                    </td>
                    <td className={row.last_log?.includes('비정상') ? 'dr-warn' : ''}>
                      {row.last_log}
                    </td>
                    <td className={row.service_st?.includes('비정상') ? 'dr-warn' : ''}>
                      {row.service_st}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
